// @flow
import React, {Component} from 'react';
import classNames from 'classnames';
import {convertPxToRem} from '@pb/kit-util-convert-px-to-rem';

import {Cell} from './data-table-cell';
import {DataTablePlaceholderCell} from './data-table-placeholder-cell';
import {
    HighlightType,
    DataTableRowSize,
    TableSize,
    tableRowName,
    HOVERED_CLASS,
    DefaultVirtualizeHorizontalData
} from './constants';

import type {Element} from 'react';
import type {VirtualizeHorizontalData} from './typedef';

type RowProps = {|
    id?: string,
    mainRow?: boolean,
    highlightType?: $Keys<typeof HighlightType>,
    mainTotal?: boolean,
    fixed?: boolean,
    index?: number,
    mainRowCollapsed?: boolean,
    setRowHeight?: (index: number, height: number, targetRow: ?HTMLDivElement) => void,
    children: Element<typeof Cell> | Array<Element<typeof Cell>>,
    onMouseEnter?: ?Function,
    onMouseLeave?: ?Function,
    virtualizeHorizontalData?: VirtualizeHorizontalData,
    rowSize?: $Keys<typeof TableSize>,
    rowHeight?: number,
    mix?: string,
|};

type State = {|
    isHovered: boolean,
|};

const HIGHLIGHT_TYPE_CLASSES = {
    [HighlightType.NONE]: null,
    [HighlightType.SUGGEST]: 'data-table__row_highlight-suggest',
    [HighlightType.INACTIVE]: 'data-table__row_highlight-inactive'
};

export class Row extends Component<RowProps, State> {
    observer: MutationObserver;
    row: ?HTMLDivElement;

    static defaultProps = {
        fixed: false,
        mainRow: false,
        mainTotal: false,
        highlightType: HighlightType.NONE,
        onMouseEnter: () => undefined,
        onMouseLeave: () => undefined,
        rowSize: TableSize.DEFAULT,
        virtualizeHorizontalData: DefaultVirtualizeHorizontalData,
        mix: ''
    };

    state = {
        isHovered: false
    };

    componentDidMount() {
        this.setHeight();

        if (!this.props.virtualizeHorizontalData.isVirtualizedCells) {
            this.observer = new MutationObserver(() => this.setHeight());

            if (this.row && this.props.setRowHeight) {
                this.observer.observe(this.row, {childList: true, subtree: true});
            }
        }
    }

    componentDidUpdate(prevProps: RowProps) {
        const {mainRow, mainRowCollapsed} = this.props;

        if (mainRow && prevProps.mainRowCollapsed !== mainRowCollapsed) {
            this.setState({isHovered: !!mainRowCollapsed});
        }
    }

    componentWillUnmount() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }

    setHeight() {
        const {setRowHeight, rowSize, index, rowHeight} = this.props;

        if (this.row && setRowHeight && index !== undefined) {
            if (rowHeight) {
                setRowHeight(index, rowHeight, this.row);
            } else {
                const height = this.row.getBoundingClientRect().height;
                if (height > DataTableRowSize[rowSize]) {
                    setRowHeight(index, height, this.row);
                }
            }
        }
    }

    setRowRef = (el: ?HTMLDivElement) => this.row = el;

    render() {
        const {
            id,
            mainRow,
            fixed,
            children,
            onMouseEnter,
            onMouseLeave,
            mainRowCollapsed,
            mainTotal,
            highlightType,
            virtualizeHorizontalData,
            mix,
            rowHeight
        } = this.props;

        const {isVirtualizedCells, leftOffset, rightOffset} = virtualizeHorizontalData;
        const style = rowHeight || rowHeight === 0 ? {'--row-height': convertPxToRem(rowHeight) || 0} : {};

        return (
            <div
                style={style}
                ref={this.setRowRef}
                id={id}
                className={
                    classNames(
                        tableRowName,
                        {
                            'data-table__row_fixed': fixed,
                            'data-table__row_main': mainRow,
                            'data-table__row_main-total': mainTotal,
                            'data-table__row_main-collapsed': mainRowCollapsed,
                            [HOVERED_CLASS]: this.state.isHovered,
                        },
                        [HIGHLIGHT_TYPE_CLASSES[highlightType]],
                        mix
                    )
                }
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}>

                <DataTablePlaceholderCell width={leftOffset} isVirtualizedCells={isVirtualizedCells}/>
                {children}
                <DataTablePlaceholderCell width={rightOffset} isVirtualizedCells={isVirtualizedCells}/>
            </div>
        );
    }
}

            // @pb/generate-flow-typed Webpack Loader:
            // Duplicates all internal dependencies imports
            // to be sure all deps are parsed by this loader
            // cause Babel loader removes `import type ...`
            // and e.g. 'typedef.js' files are not included in dist
            
require("./constants");

require("./data-table-cell");

require("./typedef");
